/**
 * Branding Write-Request Builder
 *
 * The one place the `PUT /admin-api/site/{siteId}/branding` contract is
 * expressed, because that contract is strict and asymmetric in two ways that are
 * easy to get wrong:
 *
 * 1. The body is exactly `siteId`, `colors` and `typography`. Any other key is a
 *    400 — including `version`, `assets`, `updatedAt` and `updatedBy`, which the
 *    read returns. So the request is *built*, never derived from a response: a
 *    read-modify-write cycle that echoed the record would fail on all four.
 * 2. An omitted key is treated as `null` and clears its slot. A write is a
 *    complete statement of a site's colours, so every key goes on every write.
 *
 * `BrandingWriteRequest` has no room for the rejected fields, so rule 1 is a
 * compile-time guarantee rather than a convention.
 *
 * Requirements: 5.2, 5.3, 5.4, 5.5, 5.6
 */

import {
  BRAND_COLOR_SLOTS,
  BRAND_TYPOGRAPHY_SLOTS,
  type BrandColors,
  type BrandColorsDraft,
  type BrandTypography,
} from '../models/domainModels';

/**
 * The complete `PUT` body. Exactly three fields, by construction.
 *
 * `colors` and `typography` reuse the domain types because the shapes are
 * identical — every slot present as a value or an explicit `null`. A separate
 * pair of request types would be two more things to keep in step for no gain.
 */
export interface BrandingWriteRequest {
  readonly siteId: string;
  readonly colors: BrandColors;
  readonly typography: BrandTypography;
}

/**
 * An editor field's contents as the wire wants it.
 *
 * Only a genuinely empty field means "unset". Anything else is passed through
 * verbatim, even when it is not a valid colour, so that a typo becomes a 422
 * naming the slot rather than a silent clear. Nulling an invalid value here would
 * produce a 200 with a colour quietly removed — an operator seeing success while
 * the club's brand loses a slot, which is exactly the failure the API's
 * strictness exists to prevent.
 */
const toWireValue = (value: string): string | null =>
  value.length === 0 ? null : value;

/**
 * Build the request body for a draft save.
 *
 * `carriedTypography` is required and has no default on purpose. This app has no
 * typography UI, but omitting the field would clear a club's typography on every
 * colour save, so the caller is forced to supply what it read.
 */
export function buildBrandingWriteRequest(
  siteId: string,
  colors: BrandColorsDraft,
  carriedTypography: BrandTypography
): BrandingWriteRequest {
  return {
    siteId,
    colors: BRAND_COLOR_SLOTS.reduce(
      (acc, slot) => ({ ...acc, [slot]: toWireValue(colors[slot]) }),
      {} as { [K in keyof BrandColors]: string | null }
    ),
    typography: BRAND_TYPOGRAPHY_SLOTS.reduce(
      (acc, slot) => ({ ...acc, [slot]: carriedTypography[slot] }),
      {} as { [K in keyof BrandTypography]: string | null }
    ),
  };
}
